import s from './HeroSlide.module.scss'
import SlickSlider from 'react-slick'
import React, { CSSProperties, ReactElement, useState } from 'react'
import cn from 'classnames'
import { getFastlyUrl } from '@corratech/pylot-image'
import { useIsArabic } from '@components/common/IsArabic/useIsArabic'
import { useDeviceType } from '@components/common/IsMobile/useDeviceType'
import { getVimeoPoster } from '../HeroSlideItem/HeroSlides'

type WidgetConfiguration = {
    appearance?: string
    autoplay?: boolean
    autoplaySpeed?: number
    children?: ReactElement[]
    infinite?: boolean
    isLazyLoad?: boolean
    isMenu?: boolean
    minHeight?: string
    showArrows?: boolean
    showDots?: boolean
    showEdgesOfAdjacentSlide?: boolean
    sliderHeight?: string
}

const fullScreenSliderHeight = '600px'
const compactSliderHeight = '520px'

const getShellStyle = (
    slideData: any,
    isMobile: boolean,
    showEdgesOfAdjacentSlide?: boolean
): CSSProperties => {
    const style: CSSProperties = {}
    if (slideData.backgroundType !== 'video' || showEdgesOfAdjacentSlide) {
        return style
    }
    if (slideData.backgroundColor) {
        style.backgroundColor = slideData.backgroundColor
    }
    const posterSrc =
        (isMobile && slideData.mobile_image) || slideData.desktop_image
    const posterVideoSrc =
        isMobile && slideData.videoMobileSource
            ? slideData.videoMobileSource
            : slideData.videoSrc
    const shellPoster =
        (posterSrc &&
            getFastlyUrl({
                src: posterSrc,
                quality: slideData.imageQuality,
                width: isMobile ? 800 : 1800
            })) ||
        getVimeoPoster(posterVideoSrc)
    if (shellPoster) {
        style.backgroundImage = `url(${shellPoster})`
        style.backgroundSize = 'cover'
        style.backgroundPosition = 'center'
    }
    return style
}

const HeroGroup = (props: WidgetConfiguration) => {
    const isArabic = useIsArabic()
    const [currentSlide, setCurrentSlide] = useState(0)

    const {
        appearance,
        autoplay,
        autoplaySpeed,
        children,
        infinite,
        isLazyLoad,
        isMenu,
        showArrows,
        showDots,
        showEdgesOfAdjacentSlide
    } = props
    const { isMobile } = useDeviceType()

    const resolvedHeight = showEdgesOfAdjacentSlide
        ? compactSliderHeight
        : fullScreenSliderHeight

    const heightClass =
        resolvedHeight === compactSliderHeight
            ? s['height-520']
            : s['height-600']

    let enhancedChildren = children?.map((item) => {
        item.props.data.showEdgesOfAdjacentSlide = showEdgesOfAdjacentSlide
        item.props.data.slideHeight = resolvedHeight
        return item
    })

    if (isArabic && enhancedChildren && enhancedChildren?.length > 1) {
        const [first, second, ...rest] = enhancedChildren
        enhancedChildren = [...rest, first, second]
    }

    const slideThemes =
        enhancedChildren?.map(
            (child: any) => child?.props?.data?.heroBannerTheme || 'theme-dark'
        ) || []

    const activeTheme = slideThemes[currentSlide] || 'theme-dark'

    const slideData = enhancedChildren?.[currentSlide]?.props?.data || {}
    const shellStyle = getShellStyle(
        slideData,
        isMobile,
        showEdgesOfAdjacentSlide
    )

    const sliderSettings = {
        autoplay: autoplay,
        autoplaySpeed: autoplaySpeed,
        infinite: infinite,
        arrows: !isMobile || !showEdgesOfAdjacentSlide,
        dots: isMobile && showDots,
        lazyLoad: isLazyLoad ? ('ondemand' as const) : undefined,
        centerMode: showEdgesOfAdjacentSlide,
        centerPadding: showEdgesOfAdjacentSlide && !isMobile ? '4%' : '0',
        rtl: isArabic
    }

    return (
        <div
            className={cn(s['hero-slider-main'], heightClass, {
                [s['showEdgesOfAdjacentSlide']]: showEdgesOfAdjacentSlide
            })}
            style={shellStyle}
            data-theme={activeTheme}
        >
            <SlickSlider {...sliderSettings}>{enhancedChildren}</SlickSlider>
        </div>
    )
}

export default HeroGroup
